import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save } from "lucide-react";

export default function AdminMultiCurrency() {
  const [active, setActive] = useState(false);
  const [enabled, setEnabled] = useState(["IN"]);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/merchant/config/multi-currency").then(({ data }) => {
      setActive(Boolean(data.active));
      setEnabled(data.multi_currency?.enabled_countries || ["IN"]);
      setCatalog(data.catalog || data.multi_currency?.countries || []);
    }).catch((e) => toast.error(extractErr(e))).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Multiple Currency</strong> from the App Store to set per-country prices.</Card>
      </div>
    );
  }

  const toggle = (code) => {
    setEnabled((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      return [...prev, code];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/multi-currency", { enabled_countries: enabled });
      setEnabled(data.multi_currency.enabled_countries);
      toast.success("Countries saved. Set prices on each product.");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="mc-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Multiple Currency</h1>
        <p className="text-sm text-slate-500 mt-1">Turn countries on. Then set a price for each country on add/edit product. Shoppers only see their country&apos;s price.</p>
      </div>
      <Card className="p-6 space-y-3 border-slate-200/70">
        <div className="grid sm:grid-cols-2 gap-2">
          {catalog.map((c) => {
            const on = enabled.includes(c.code);
            return (
              <label key={c.code} className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm cursor-pointer ${on ? "border-violet-400 bg-violet-50" : "border-slate-200"}`} data-testid={`mc-country-${c.code}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(c.code)} />
                <span className="font-medium">{c.name}</span>
                <span className="text-slate-500 ml-auto">{c.symbol} {c.currency}</span>
              </label>
            );
          })}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-violet-600 hover:bg-violet-700" data-testid="mc-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save countries"}
        </Button>
      </Card>
    </div>
  );
}
